import React from "react";
import { Link } from "react-router-dom";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import "./group-accommodation.css";

const galleryImages = [
  { src: "/design/assets/accom-2.jpg", alt: "Underground bunkhouse interior" },
  { src: "/design/assets/accom-3.jpg", alt: "Underground rooms for group accommodation" },
  { src: "/design/assets/accom-4.jpg", alt: "Underground bunkhouse living space" },
  { src: "/design/assets/accom-5.jpg", alt: "Climate-controlled underground accommodation" },
  { src: "/design/assets/accom-bunk.jpg", alt: "Bunk beds with mattresses" },
  { src: "/design/assets/accom-1.jpg", alt: "Underground group accommodation at Umoona" },
];

const RGroupAccommodationPage = () => {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    name: "Umoona Underground Group Accommodation",
    description:
      "Unique underground accommodation for groups in Coober Pedy. 24°C constant temperature, bunkhouse accommodation for up to 102 people.",
    address: {
      "@type": "PostalAddress",
      streetAddress: "14 Hutchison St",
      addressLocality: "Coober Pedy",
      addressRegion: "SA",
      postalCode: "5723",
      addressCountry: "AU",
    },
    url: "https://umoonaopalmine.com.au/group-accommodation",
  };

  return (
    <MarketingShell pageClass="pg-group-accommodation">
      <SEOHead
        title="Underground Group Accommodation - Coober Pedy | Umoona Opal Mine"
        description="Unique underground accommodation for groups in Coober Pedy. 24°C constant temperature, bunkhouse accommodation for up to 102 people."
        keywords="underground accommodation, group accommodation Coober Pedy, school group accommodation, underground bunkhouses"
        canonical="/group-accommodation"
        schemaData={schemaData}
      />

      {/* HERO */}
      <div className="u-hero">
        <img
          className="u-hero__img"
          src="/design/assets/accom-1.jpg"
          alt="Underground Group Accommodation at Umoona"
        />
        <div className="u-hero__scrim" />
        <div className="u-hero__inner">
          <div className="u-hero__eyebrow">Underground living · 24°C constant</div>
          <h1 className="u-hero__title">Commercial Group Tours &amp; Accommodation</h1>
          <p className="u-hero__sub">
            Umoona Opal Mine offers unique underground accommodation for groups big and small —
            comfortable year-round, whatever the desert does above.
          </p>
        </div>
      </div>

      {/* STATS + UNDERGROUND BUNKHOUSE ACCOMMODATION */}
      <section className="u-sectpad u-sect-deep">
        <div className="u-grid3 ga-stats">
          <div className="ga-card">
            <div className="ga-card__kicker">MAX BEDS</div>
            <div className="ga-card__title">102</div>
            <p className="ga-card__text">Our largest bunkhouse sleeps up to 102.</p>
          </div>
          <div className="ga-card">
            <div className="ga-card__kicker ga-card__kicker--turquoise">TEMPERATURE</div>
            <div className="ga-card__title">24°C</div>
            <p className="ga-card__text">A constant, comfortable temperature all year.</p>
          </div>
          <div className="ga-card">
            <div className="ga-card__kicker ga-card__kicker--teal">PER ROOM</div>
            <div className="ga-card__title">4–6 share</div>
            <p className="ga-card__text">Bunkhouses of various sizes to suit your group.</p>
          </div>
        </div>
        <div className="u-eyebrow">Underground bunkhouse accommodation</div>
        <h2 className="u-h2">
          We provide accommodation in underground bunkhouses, 4 to 6 share. Each is a different
          size — and our largest has 102 beds.
        </h2>
        <div className="u-grid2">
          <div className="ga-card">
            <div className="ga-card__kicker">THE ROOMS</div>
            <div className="ga-card__title">Bunks &amp; mattresses</div>
            <p className="ga-card__text">
              All rooms have bunk beds with mattresses. Linen can be supplied if required.
            </p>
          </div>
          <div className="ga-card">
            <div className="ga-card__kicker ga-card__kicker--teal">FACILITIES</div>
            <div className="ga-card__title">Kitchen &amp; bathrooms</div>
            <p className="ga-card__text">
              Fridge, sink, kettle and toaster provided. Toilet facilities and water-metered
              showers for each group.
            </p>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="u-sectpad u-sect-test">
        <div className="u-eyebrow">The bunkhouse</div>
        <h2 className="u-h2">Gallery</h2>
        <div className="u-grid3 ga-gallery">
          {galleryImages.map((image, index) => (
            <div key={index} className="ga-gallery__item">
              <img src={image.src} alt={image.alt} loading="lazy" decoding="async" />
            </div>
          ))}
        </div>
      </section>

      {/* PERFECT FOR GROUP TRAVEL */}
      <section className="u-sectpad u-sect-deep">
        <div className="u-eyebrow">Perfect for group travel</div>
        <h2 className="u-h2">Who stays with us</h2>
        <div className="u-grid3">
          <div className="ga-card">
            <div className="ga-card__kicker">SCHOOLS</div>
            <div className="ga-card__title">School groups</div>
            <p className="ga-card__text">
              A safe, memorable, educational underground stay for student groups.
            </p>
          </div>
          <div className="ga-card">
            <div className="ga-card__kicker ga-card__kicker--teal">TOUR GROUPS</div>
            <div className="ga-card__title">Coach &amp; tour groups</div>
            <p className="ga-card__text">
              Comfortable underground beds for touring parties of all sizes.
            </p>
          </div>
          <div className="ga-card">
            <div className="ga-card__kicker ga-card__kicker--turquoise">SPECIAL GROUPS</div>
            <div className="ga-card__title">Clubs &amp; more</div>
            <p className="ga-card__text">
              Unique group experiences for clubs, teams and special-interest travellers.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="u-sectpad u-sect-cta">
        <div className="u-ctacenter">
          <h2 className="u-ctacenter__title">Ready for your underground group experience?</h2>
          <p className="u-ctacenter__sub">
            Contact us to learn more about our unique underground accommodation and plan your
            authentic Coober Pedy group visit.
          </p>
          <div className="u-ctacenter__btns">
            <Link to="/contact" className="u-btn u-btn--primary">
              Contact for Info
            </Link>
            <Link to="/group-tours" className="u-btn u-btn--ghost">
              Group Tours
            </Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
};

export default RGroupAccommodationPage;
